<script setup lang="ts">
/**
 * AbstractImage
 *
 * Renders a deterministic decorative SVG from a `seed` string (e.g. an article
 * title). The same seed always produces the same image, so it is stable across
 * reloads and needs no network, storage, or API. Use it as a fallback wherever
 * a real cover image may be missing. Generation lives in `useAbstractImage`.
 */
import { buildAbstractScene, VIEW_W, VIEW_H, type AbstractVariant } from '~/composables/useAbstractImage'

const props = defineProps({
  /** Any string — same seed always yields the same image. */
  seed: {
    type: String,
    default: '',
  },
  /** Generative vocabulary: soft blobs, triangulation, or flat geometry. */
  variant: {
    type: String as () => AbstractVariant,
    default: 'mesh',
  },
  width: {
    type: [Number, String],
    default: '100%',
  },
  height: {
    type: [Number, String],
    default: 200,
  },
  /** Optional hex (e.g. category color) to anchor the palette's base hue. */
  baseColor: {
    type: String,
    default: '',
  },
  /** Optional text drawn over the image; falls back to the default slot. */
  title: {
    type: String,
    default: '',
  },
  /** Vertical placement of the overlay/slot content. */
  overlayAlign: {
    type: String as () => 'center' | 'end' | 'start',
    default: 'center',
  },
})

const uid = useId()

const scene = computed(() =>
  buildAbstractScene({ seed: props.seed, variant: props.variant, baseColor: props.baseColor })
)

const overlayJustify = computed(() => ({ center: 'center', start: 'flex-start', end: 'flex-end' })[props.overlayAlign])

const sizeStyle = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  height: typeof props.height === 'number' ? `${props.height}px` : props.height,
}))

const BLUR_FILTER = { far: 'blurFar', mid: 'blurMid', near: 'blurNear' } as const
const blurFor = (level: 'far' | 'mid' | 'near') => `url(#${BLUR_FILTER[level]}-${uid})`
</script>

<template>
  <div class="abstract-image" :style="sizeStyle">
    <svg
      class="abstract-image__svg"
      :viewBox="`0 0 ${VIEW_W} ${VIEW_H}`"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      :aria-label="title || 'Decorative image'"
    >
      <defs>
        <linearGradient :id="`bg-${uid}`" :gradientTransform="`rotate(${scene.gradAngle} 0.5 0.5)`">
          <stop offset="0%" :stop-color="scene.bgFrom" />
          <stop offset="100%" :stop-color="scene.bgTo" />
        </linearGradient>
        <filter :id="`blurFar-${uid}`" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="46" />
        </filter>
        <filter :id="`blurMid-${uid}`" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="24" />
        </filter>
        <filter :id="`blurNear-${uid}`" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <filter :id="`grain-${uid}`">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>

      <rect :width="VIEW_W" :height="VIEW_H" :fill="`url(#bg-${uid})`" />

      <!-- mesh: layered blurred blobs -->
      <circle
        v-for="(b, i) in scene.blobs"
        :key="`b${i}`"
        :cx="b.cx"
        :cy="b.cy"
        :r="b.r"
        :fill="b.fill"
        :opacity="b.opacity"
        :filter="blurFor(b.blur)"
      />

      <!-- lowpoly: gradient-coloured triangulation -->
      <polygon
        v-for="(t, i) in scene.triangles"
        :key="`t${i}`"
        :points="t.points"
        :fill="t.fill"
        :stroke="t.fill"
        stroke-width="1"
      />

      <!-- bauhaus: flat geometric primitives -->
      <template v-for="(s, i) in scene.shapes" :key="`s${i}`">
        <circle v-if="s.kind === 'circle'" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
        <circle
          v-else-if="s.kind === 'ring'"
          :cx="s.cx"
          :cy="s.cy"
          :r="s.r"
          fill="none"
          :stroke="s.fill"
          :stroke-width="(s.r || 10) * 0.28"
          :opacity="s.opacity"
        />
        <path
          v-else-if="s.kind === 'quarter'"
          :d="s.d"
          :fill="s.fill"
          :opacity="s.opacity"
          :transform="`rotate(${s.rot} ${s.cx} ${s.cy})`"
        />
        <rect
          v-else
          :x="s.x"
          :y="s.y"
          :width="s.w"
          :height="s.h"
          :fill="s.fill"
          :opacity="s.opacity"
          :transform="`rotate(${s.rot || 0} ${(s.x || 0) + (s.w || 0) / 2} ${(s.y || 0) + (s.h || 0) / 2})`"
        />
      </template>

      <!-- leading lines radiating from the focal point -->
      <line
        v-for="(l, i) in scene.lines"
        :key="`l${i}`"
        :x1="l.x1"
        :y1="l.y1"
        :x2="l.x2"
        :y2="l.y2"
        :stroke="l.stroke"
        :stroke-width="l.width"
        :opacity="l.opacity"
      />

      <!-- subtle film grain for a finished, textured look -->
      <rect
        :width="VIEW_W"
        :height="VIEW_H"
        :filter="`url(#grain-${uid})`"
        opacity="0.12"
        style="mix-blend-mode: overlay"
      />
    </svg>

    <div v-if="title || $slots.default" class="abstract-image__overlay" :style="{ justifyContent: overlayJustify }">
      <slot>
        <span class="abstract-image__title">{{ title }}</span>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.abstract-image {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
}

.abstract-image__svg {
  display: block;
  width: 100%;
  height: 100%;
}

.abstract-image__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  color: #fff;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
}

.abstract-image__title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}
</style>
